const STEP_KEYS_BY_INDEX = [
  'first_name',
  'language',
  'native_language',
  'coach_intro',
  'level',
  'motivation',
  'interests',
  'practice_frequency',
  'practice_time',
  'acquisition_source'
];

const LANGUAGE_DIALECT_STEP = 'language_dialect';
const LANGUAGE_STEP_INDEX = STEP_KEYS_BY_INDEX.indexOf('language');
const ORDER_WITH_DIALECT = [
  ...STEP_KEYS_BY_INDEX.slice(0, LANGUAGE_STEP_INDEX + 1),
  LANGUAGE_DIALECT_STEP,
  ...STEP_KEYS_BY_INDEX.slice(LANGUAGE_STEP_INDEX + 1)
];

function hasValue(value) {
  return value !== undefined && value !== null && value !== '';
}

function hasDialectForLanguage(state) {
  const language = state && state.data ? state.data.learningLanguage : null;
  const dialects = state ? state.languageDialects : null;

  if (state && state.isDialectPage) return true;
  if (state && state.data && hasValue(state.data.preferredDialect)) return true;
  if (!language || !dialects) return false;

  return Object.prototype.hasOwnProperty.call(dialects, language);
}

function isBeginner(state) {
  return !!(state && state.data && state.data.languageLevel === 'beginner');
}

function stepCountFor(state) {
  let count = STEP_KEYS_BY_INDEX.length;
  if (isBeginner(state)) count -= 1;
  if (hasDialectForLanguage(state)) count += 1;
  return count;
}

function logicalOrderFor(state) {
  const order = hasDialectForLanguage(state) ? ORDER_WITH_DIALECT.slice() : STEP_KEYS_BY_INDEX.slice();
  if (isBeginner(state)) {
    return order.filter((key) => key !== 'interests');
  }
  return order;
}

function screenMovedForward(previousKey, nextKey, state) {
  const order = logicalOrderFor(state);
  const previousIndex = order.indexOf(previousKey);
  const nextIndex = order.indexOf(nextKey);

  return previousIndex !== -1 && nextIndex !== -1 && nextIndex > previousIndex;
}

function screenKeyFor(state) {
  if (!state) return null;
  if (state.currentStep === LANGUAGE_STEP_INDEX && state.isDialectPage) return LANGUAGE_DIALECT_STEP;
  return STEP_KEYS_BY_INDEX[state.currentStep] || null;
}

function commonProperties(step, onboardingVariant) {
  return {
    surface: 'web',
    platform: 'web',
    onboarding_variant: onboardingVariant,
    step: step,
    step_index: step === LANGUAGE_DIALECT_STEP
      ? LANGUAGE_STEP_INDEX
      : STEP_KEYS_BY_INDEX.indexOf(step),
    is_reprompt: false
  };
}

function addIfPresent(properties, key, value) {
  if (hasValue(value)) properties[key] = value;
}

function completionProperties(step, state, onboardingVariant, revisit) {
  const properties = Object.assign(commonProperties(step, onboardingVariant), {
    revisit: !!revisit
  });
  const data = state && state.data ? state.data : {};

  switch (step) {
    case 'language':
      addIfPresent(properties, 'language', data.learningLanguage);
      break;
    case LANGUAGE_DIALECT_STEP:
      addIfPresent(properties, 'preferred_dialect', data.preferredDialect);
      break;
    case 'native_language':
      addIfPresent(properties, 'native_language', data.nativeLanguage);
      break;
    case 'coach_intro':
      addIfPresent(properties, 'coach_id', data.coachData && data.coachData.voice_key);
      addIfPresent(properties, 'advance_reason', state && state.coachIntroAdvanceReason);
      break;
    case 'level':
      addIfPresent(properties, 'level', data.languageLevel);
      break;
    case 'motivation':
      addIfPresent(properties, 'motivation', data.motivation);
      break;
    case 'interests': {
      const interestsCount = Array.isArray(data.interests) ? data.interests.length : 0;
      const customInterestsCount = Array.isArray(data.customInterests) ? data.customInterests.length : 0;
      properties.interests_count = interestsCount;
      properties.custom_interests_count = customInterestsCount;
      properties.skipped = interestsCount + customInterestsCount === 0;
      break;
    }
    case 'practice_frequency':
      addIfPresent(properties, 'practice_frequency', data.practiceFrequency);
      break;
    case 'practice_time':
      addIfPresent(properties, 'practice_time_goal', data.practiceTimeGoal);
      break;
    case 'acquisition_source':
      addIfPresent(properties, 'acquisition_source', data.acquisitionSource);
      properties.step_count = stepCountFor(state);
      break;
  }

  return properties;
}

function createWebOnboardingAnalytics({ capture, onboardingVariant }) {
  const seenSteps = new Set();
  const completedSteps = new Set();
  let lastScreenKey = null;

  function captureCompleted(step, state, captureOptions) {
    if (!step) return;
    const revisit = completedSteps.has(step);
    capture('onboarding_step_completed', completionProperties(step, state, onboardingVariant, revisit), captureOptions);
    completedSteps.add(step);
  }

  return {
    trackTransition(state) {
      const nextScreenKey = screenKeyFor(state);

      if (!nextScreenKey || nextScreenKey === lastScreenKey) {
        return;
      }

      if (lastScreenKey && screenMovedForward(lastScreenKey, nextScreenKey, state)) {
        captureCompleted(lastScreenKey, state);
      }

      capture(
        'onboarding_step_viewed',
        Object.assign(commonProperties(nextScreenKey, onboardingVariant), {
          revisit: seenSteps.has(nextScreenKey)
        })
      );

      seenSteps.add(nextScreenKey);
      lastScreenKey = nextScreenKey;
    },

    trackTerminalCompletion(state, captureOptions) {
      if (screenKeyFor(state) === 'acquisition_source') {
        captureCompleted('acquisition_source', state, captureOptions);
      }
    }
  };
}

module.exports = { createWebOnboardingAnalytics };
